import { PersistentServiceIcon, ServiceIcon } from '@assets/icons';
import { Group } from '@components/layout';
import { InternalLink } from '@components/link';
import { DateDisplay, DateToNowDisplay, Typography } from '@components/typography';
import { ServiceTypeEnum, type Service } from '@generated/graphql';
import { Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ServerTag } from '../../ServerTag';

type ServiceDetailsModalProps = {
  service?: Partial<Service>;
  organizationId: string;
};

const DetailRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Stack flex={1}>
    <Typography
      variant="caption2"
      color="foreground.paper.light"
      sx={{ minWidth: 100, flexShrink: 0 }}
    >
      {label}
    </Typography>
    {children}
  </Stack>
);

export const ServiceDetailsModal = ({ service, organizationId }: ServiceDetailsModalProps) => {
  const { palette } = useTheme();
  const isPersistent = service.serviceType === ServiceTypeEnum.PERSISTENT_SERVICE;
  const isMarketplace = service.serviceType === ServiceTypeEnum.MARKETPLACE;

  return (
    <>
      <Stack gap={1} mb={6}>
        <Group alignItems="center" gap={2}>
          {isPersistent ? (
            <PersistentServiceIcon size={21} color={palette.foreground.paper.default} />
          ) : (
            <ServiceIcon size={21} color={palette.foreground.paper.default} />
          )}
          <Typography variant="h2" handleOverFlow sx={{ flex: 1, minWidth: 0 }}>
            {service.name}
          </Typography>
        </Group>
        {isPersistent && (
          <ServerTag
            variant="caption2"
            color="default"
            label="Persistent"
            sx={{ width: 'fit-content' }}
          />
        )}
        {isMarketplace && (
          <ServerTag
            variant="caption2"
            color="default"
            label="Marketplace"
            sx={{ width: 'fit-content' }}
          />
        )}
      </Stack>
      <Stack gap={4} sx={{ maxWidth: '450px' }}>
        <Typography variant="body2" color="foreground.paper.light">
          {service.description || 'No description'}
        </Typography>
        <Stack gap={2}>
          <Group gap={4}>
            <DetailRow label="Created">
              <DateDisplay date={service.createdAt || ''} />
            </DetailRow>
            <DetailRow label="Last Updated">
              <DateToNowDisplay date={service.updatedAt || ''} />
            </DetailRow>
          </Group>
          {service.serviceId && (
            <InternalLink to={`/${organizationId}/services/${service.serviceId}`}>
              View service page
            </InternalLink>
          )}
        </Stack>
      </Stack>
    </>
  );
};
